import { putCmProgram, getCmProgramGroup } from '@/api'
import { ref, isRef, watch } from '@vue/composition-api'
import store from '@/store'
import { useLoading } from '@coursedog/composables'
import {
  buildFromDataKeyToQuestionId,
  buildFromQuestionIdToDataKey,
  unflattenCustomQuestions
} from '@/utils/formBuilder'
import _clonedeep from 'lodash/cloneDeep'
import { RELATED_DATA_COURSES_PROJECTION, RELATED_DATA_PROGRAMS_PROJECTION } from './constants'

export default function useProgram ({
  id,
  school = store.getters.school,
  questions,
} = {}) {
  const programId = isRef(id) ? id : ref(id)
  let programData
  const program = ref({})
  const programs = ref([])
  const initialProgram = ref({})
  const { startLoading, stopLoading, getIsLoading } = useLoading()
  let customFieldsKeys = []

  async function getProgram () {
    startLoading('getProgram')
    programData = await store.dispatch('curriculumPrograms/getProgram', {
      id: programId.value,
      relatedDataCoursesProjection: RELATED_DATA_COURSES_PROJECTION,
      relatedDataProgramsProjection: RELATED_DATA_PROGRAMS_PROJECTION,
    })

    const { customFields = {}, ...value } = programData
    customFieldsKeys = Object.keys(customFields)

    const adjustedValue = buildFromDataKeyToQuestionId(questions.value, value)

    program.value = {
      ...value,
      ...adjustedValue,
      ...customFields,
    }
    backupProgram()
    stopLoading('getProgram')
  }
  watch(programId, getProgram)

  async function saveProgram (options = {}) {
    startLoading('saveProgram')

    const { adjustedValue, nonQuestionValue } = buildFromQuestionIdToDataKey(questions.value, program.value)
    const nonQuestionCustomFieldsMockQuestions = []
    const nonQuestionCustomFields = customFieldsKeys.reduce((obj, dataKey) => {
      if (nonQuestionValue.hasOwnProperty(dataKey)) {
        obj[dataKey] = nonQuestionValue[dataKey]
        nonQuestionCustomFieldsMockQuestions.push({ dataKey, custom: true })
      }
      return obj
    }, {})

    const programValue = unflattenCustomQuestions(
      {
        ...adjustedValue,
        ...nonQuestionCustomFields,
      },
      [
        ...questions.value,
        ...nonQuestionCustomFieldsMockQuestions,
      ]
    )

    // Null eff dates if the questions didn't have them, so API can look them up
    // based on the terms. Null the values here to make sure nothing put the possibly
    // wrong eff dates back in.

    const questionKeys = questions.value.map(q => q.dataKey || q.questionId)

    if (!questionKeys.includes('effectiveStartDate')) {
      programValue.effectiveStartDate = null
    }

    if (!questionKeys.includes('effectiveEndDate')) {
      programValue.effectiveEndDate = null
    }

    const resp = await putCmProgram({
      ...programData,
      school,
      ...programValue,
      version: program.value.version || programData.version,
      programId: programId.value,
      _id: programId.value,
      id: programId.value,
    }, options)

    const newId = resp.id || programId.value

    program.value.version = resp.version || program.value.version
    program.value._id = newId
    program.value.id = newId
    backupProgram()
    stopLoading('saveProgram')
  }

  function backupProgram () {
    initialProgram.value = _clonedeep(program.value)
  }

  function resetProgram () {
    program.value = _clonedeep(initialProgram.value)
  }

  function generateProgramLabel (program) {
    if (!program) return 'Program Not Found'
    const code = program.code || program.name || ''
    const name = program.transcriptDescription || program.longName || program.name || 'Untitled Program'
    const appendRequestStatus = [ 'pending', 'suspended', 'rejected' ].includes(program.requestStatus)
    if (appendRequestStatus) {
      return `${code} - ${name} (${program.requestStatus} in workflow)`
    }
    if (program.status === 'Inactive') {
      return `${code} - ${name} (Inactive)`
    }
    return `${code} - ${name}`
  }

  async function fetchProgramsByProgramGroupId (programGroupId) {
    startLoading('getPrograms')
    programs.value = await getCmProgramGroup({ school, programGroupId })
    stopLoading('getPrograms')
  }

  function setProgram (programRevision) {
    const { customFields = {}, ...value } = programRevision
    const adjustedValue = buildFromDataKeyToQuestionId(questions.value, value)
    program.value = {
      ...value,
      ...adjustedValue,
      ...customFields,
    }
    backupProgram()
  }

  return {
    program,
    programs,
    getProgram,
    saveProgram,
    fetchProgramsByProgramGroupId,
    resetProgram,
    setProgram,
    generateProgramLabel,
    isLoading: getIsLoading('getProgram'),
    isLoadingPrograms: getIsLoading('getPrograms'),
    isSaving: getIsLoading('saveProgram'),
  }
}
